import { ref, shallowRef } from 'vue';
import { apiUrl } from '../net';

export interface ViewersInfo {
  current: number;
  max: number;
}

export interface StreamStats {
  fps: number;
  bitrateKbps: number;
  packetsLost: number;
  jitterMs: number;
  freezeCount: number;
  rttMs: number;
  candidateType: string;
}

export function useWhepPlayer(
  sendSignalingMessage: (msg: any) => void,
  onFallback?: (reason: string) => void
) {
  const status = ref<'idle' | 'connecting' | 'playing' | 'full' | 'error'>('idle');
  const errorMessage = ref<string | null>(null);
  const fullInfo = ref<{ current: number; max: number; streamerName: string } | null>(null);
  const viewers = ref<ViewersInfo>({ current: 0, max: 2 });

  const remoteVideoStream = shallowRef<MediaStream | null>(null);
  const remoteAudioStream = shallowRef<MediaStream | null>(null);

  const stats = ref<StreamStats>({
    fps: 0,
    bitrateKbps: 0,
    packetsLost: 0,
    jitterMs: 0,
    freezeCount: 0,
    rttMs: 0,
    candidateType: 'host',
  });

  let pc: RTCPeerConnection | null = null;
  let currentRequestId = '';
  let connectTimer: number | undefined;
  let retryTimer: number | undefined;
  let disconnectTimer: number | undefined;
  let retryCount = 0;
  let lastArgs: { roomId: string; userId: string; streamerId: string } | null = null;
  const MAX_RETRIES = 5;
  const CONNECT_TIMEOUT_MS = 15000;

  function clearTimers() {
    if (connectTimer) { clearTimeout(connectTimer); connectTimer = undefined; }
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = undefined; }
    if (disconnectTimer) { clearTimeout(disconnectTimer); disconnectTimer = undefined; }
  }

  function scheduleRetry(reason: string) {
    console.warn(`[WHEP Player] Falha (${reason}). Tentativa ${retryCount + 1}/${MAX_RETRIES}`);
    stopPlaying(false, true);
    if (!lastArgs || retryCount >= MAX_RETRIES) {
      status.value = 'error';
      errorMessage.value = reason;
      return;
    }
    const delay = Math.min(2000 * Math.pow(1.5, retryCount), 8000);
    retryCount++;
    status.value = 'connecting';
    const args = lastArgs;
    retryTimer = window.setTimeout(() => {
      startPlaying(args.roomId, args.userId, args.streamerId, true);
    }, delay);
  }

  function handleError(requestId: string, reason: string) {
    if (requestId && requestId !== currentRequestId) return;
    const msg = reason === 'stream-not-ready'
      ? 'A transmissão ainda não está pronta no transmissor.'
      : reason === 'stream-full'
        ? 'Transmissão cheia.'
        : reason || 'Erro na negociação WHEP';
    scheduleRetry(msg);
  }

  function retryNow() {
    if (!lastArgs) return;
    retryCount = 0;
    startPlaying(lastArgs.roomId, lastArgs.userId, lastArgs.streamerId, true);
  }
  let statsTimer: number | undefined;
  let lastBytesReceived = 0;
  let lastStatsTime = 0;
  let lastFramesDecoded = 0;

  async function getIceServers(): Promise<RTCConfiguration> {
    try {
      const res = await fetch(apiUrl('/api/ice'));
      if (res.ok) {
        const data = await res.json();
        if (data.iceServers && data.iceServers.length > 0) {
          return {
            iceServers: data.iceServers,
            bundlePolicy: 'max-bundle',
          };
        }
      }
    } catch (e) {}

    return {
      iceServers: [
        { urls: 'stun:stun.cloudflare.com:3478' },
        { urls: 'stun:stun.l.google.com:19302' },
      ],
      bundlePolicy: 'max-bundle',
    };
  }

  async function startPlaying(roomId: string, userId: string, streamerId: string, isRetry = false) {
    stopPlaying();
    if (!isRetry) retryCount = 0;
    lastArgs = { roomId, userId, streamerId };
    status.value = 'connecting';
    connectTimer = window.setTimeout(() => {
      if (status.value === 'connecting') scheduleRetry('Tempo esgotado ao conectar com o transmissor.');
    }, CONNECT_TIMEOUT_MS);
    errorMessage.value = null;
    fullInfo.value = null;

    currentRequestId = 'req-' + Math.random().toString(36).substring(2, 9);
    console.log(`[WHEP Player] Iniciando conexão para streamer ${streamerId}, requestId=${currentRequestId}...`);

    try {
      const w = typeof window !== 'undefined' ? (window as any) : null;
      if (w && typeof w.RTCPeerConnection !== 'function') {
        try {
          let bridge = document.getElementById('nexia-webrtc-bridge') as HTMLIFrameElement | null;
          if (!bridge) {
            bridge = document.createElement('iframe');
            bridge.id = 'nexia-webrtc-bridge';
            bridge.style.display = 'none';
            (document.body || document.documentElement).appendChild(bridge);
          }
          const cw = bridge.contentWindow as any;
          if (cw?.RTCPeerConnection) {
            w.RTCPeerConnection = cw.RTCPeerConnection;
            if (cw.RTCSessionDescription) w.RTCSessionDescription = cw.RTCSessionDescription;
            if (cw.RTCIceCandidate) w.RTCIceCandidate = cw.RTCIceCandidate;
            if (cw.MediaStream) w.MediaStream = cw.MediaStream;
            if (cw.RTCRtpReceiver) w.RTCRtpReceiver = cw.RTCRtpReceiver;
          }
        } catch (e) {}
      }

      const config = await getIceServers();
      pc = new RTCPeerConnection(config);

      // Transceivers de recepção pura
      const videoTransceiver = pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });

      // Força prioridade de H.264 para casar perfeitamente com o encoder do OBS / MediaMTX
      if (typeof RTCRtpReceiver !== 'undefined' && RTCRtpReceiver.getCapabilities) {
        const videoCodecs = RTCRtpReceiver.getCapabilities('video')?.codecs || [];
        const h264Codecs = videoCodecs.filter(c => c.mimeType.toLowerCase() === 'video/h264');
        if (h264Codecs.length > 0) {
          try {
            videoTransceiver.setCodecPreferences(h264Codecs);
          } catch (e) {}
        }
      }

      pc.ontrack = (event) => {
        const track = event.track;
        console.log(`[WHEP Player] Track recebida: ${track.kind} (${track.id})`);

        const stream = (event.streams && event.streams.length > 0 && event.streams[0])
          ? event.streams[0]
          : null;

        if (track.kind === 'video') {
          track.contentHint = 'motion';
          remoteVideoStream.value = stream || new MediaStream([track]);
        } else if (track.kind === 'audio') {
          track.enabled = true;
          track.contentHint = 'music';
          remoteAudioStream.value = stream || new MediaStream([track]);
        }

        clearTimers();
        status.value = 'playing';

        // Configura jitter buffer alvo de 80ms para ultra-fluidez e lip-sync
        if (event.receiver) {
          try {
            if ('jitterBufferTarget' in event.receiver) {
              (event.receiver as any).jitterBufferTarget = 80;
            }
          } catch (e) {}
        }
      };

      pc.onconnectionstatechange = () => {
        console.log('[WHEP Player] Connection state:', pc?.connectionState);
        if (pc?.connectionState === 'connected') {
          clearTimers();
          retryCount = 0;
          status.value = 'playing';
          startStatsMonitoring();
        } else if (pc?.connectionState === 'failed') {
          scheduleRetry('Falha na conexão WebRTC (ICE) com o transmissor.');
        } else if (pc?.connectionState === 'disconnected') {
          if (disconnectTimer) clearTimeout(disconnectTimer);
          disconnectTimer = window.setTimeout(() => {
            if (pc?.connectionState === 'disconnected') scheduleRetry('Conexão perdida com o transmissor.');
          }, 5000);
        }
      };

      pc.oniceconnectionstatechange = () => {
        console.log('[WHEP Player] ICE state:', pc?.iceConnectionState);
      };
      pc.onicecandidate = (e) => {
        if (e.candidate) console.log('[WHEP Player] Candidato local:', e.candidate.type, e.candidate.protocol);
      };

      const offer = await pc.createOffer();
      const rawOfferSdp = offer.sdp;
      await pc.setLocalDescription(offer);

      // Aguarda ICE gathering completo (com timeout de 2s)
      await new Promise<void>((resolve) => {
        if (!pc || pc.iceGatheringState === 'complete') {
          return resolve();
        }
        const timer = setTimeout(() => {
          pc?.removeEventListener('icegatheringstatechange', checkGathering);
          resolve();
        }, 2000);

        function checkGathering() {
          if (pc?.iceGatheringState === 'complete') {
            clearTimeout(timer);
            pc?.removeEventListener('icegatheringstatechange', checkGathering);
            resolve();
          }
        }

        pc.addEventListener('icegatheringstatechange', checkGathering);
      });

      // Se pc foi abortado/fechado durante o gathering, interrompe o envio
      if (!pc || pc.signalingState === 'closed') {
        console.warn('[WHEP Player] Conexão abortada antes do envio da oferta.');
        return;
      }

      const sdpToSend = pc.localDescription?.sdp || rawOfferSdp;
      if (!sdpToSend || !sdpToSend.startsWith('v=0')) {
        console.warn('[WHEP Player] SDP de oferta inválido, ignorando envio.');
        return;
      }

      // Envia oferta WHEP ao Worker
      sendSignalingMessage({
        type: 'whep-offer',
        roomId,
        senderId: userId,
        targetId: streamerId,
        payload: {
          requestId: currentRequestId,
          sdp: sdpToSend,
        },
      });
    } catch (err: any) {
      console.error('[WHEP Player] Erro ao iniciar player:', err);
      status.value = 'error';
      errorMessage.value = err.message || 'Erro ao inicializar conexão';
    }
  }

  async function handleAnswer(requestId: string, answerSdp: string) {
    if (requestId !== currentRequestId || !pc) return;

    try {
      console.log(`[WHEP Player] Aplicando resposta SDP WHEP para ${requestId}...`);
      await pc.setRemoteDescription(new RTCSessionDescription({
        type: 'answer',
        sdp: answerSdp,
      }));
    } catch (err: any) {
      console.error('[WHEP Player] Erro ao aplicar answer:', err);
      scheduleRetry('Erro ao processar resposta do servidor');
    }
  }

  function handleStreamFull(payload: { current: number; max: number; streamerName: string }) {
    console.warn('[WHEP Player] Transmissão cheia:', payload);
    status.value = 'full';
    fullInfo.value = payload;
    viewers.value = { current: payload.current, max: payload.max };
    stopPlaying(false);
  }

  function handleViewersUpdate(payload: ViewersInfo) {
    if (payload) {
      viewers.value = payload;
    }
  }

  function startStatsMonitoring() {
    if (statsTimer) clearInterval(statsTimer);
    lastBytesReceived = 0;
    lastStatsTime = performance.now();
    lastFramesDecoded = 0;

    statsTimer = window.setInterval(async () => {
      if (!pc || pc.connectionState !== 'connected') return;

      try {
        const report = await pc.getStats();
        const now = performance.now();
        const timeDiff = (now - lastStatsTime) / 1000;

        report.forEach((stat) => {
          if (stat.type === 'inbound-rtp' && stat.kind === 'video') {
            if (lastBytesReceived > 0 && timeDiff > 0) {
              const bytesDiff = stat.bytesReceived - lastBytesReceived;
              stats.value.bitrateKbps = Math.round((bytesDiff * 8) / 1000 / timeDiff);
            }
            if (lastFramesDecoded > 0 && timeDiff > 0 && stat.framesDecoded !== undefined) {
              const framesDiff = stat.framesDecoded - lastFramesDecoded;
              stats.value.fps = Math.round(framesDiff / timeDiff);
            }
            stats.value.packetsLost = stat.packetsLost || 0;
            stats.value.jitterMs = Math.round((stat.jitter || 0) * 1000);
            stats.value.freezeCount = stat.freezeCount || 0;

            lastBytesReceived = stat.bytesReceived;
            lastFramesDecoded = stat.framesDecoded || 0;
            lastStatsTime = now;
          }

          if (stat.type === 'candidate-pair' && stat.state === 'succeeded') {
            stats.value.rttMs = Math.round((stat.currentRoundTripTime || 0) * 1000);
          }
        });
      } catch (e) {}
    }, 1000);
  }

  function stopPlaying(notifyServer = true, keepRetry = false) {
    if (!keepRetry) clearTimers();
    else if (connectTimer) { clearTimeout(connectTimer); connectTimer = undefined; }
    if (statsTimer) {
      clearInterval(statsTimer);
      statsTimer = undefined;
    }

    if (notifyServer && currentRequestId) {
      sendSignalingMessage({
        type: 'whep-stop',
        roomId: lastArgs?.roomId,
        senderId: lastArgs?.userId,
        targetId: lastArgs?.streamerId,
        payload: { requestId: currentRequestId },
      });
    }

    if (pc) {
      try {
        pc.close();
      } catch (e) {}
      pc = null;
    }

    remoteVideoStream.value = null;
    remoteAudioStream.value = null;
    currentRequestId = '';
    if (status.value !== 'full' && !keepRetry) {
      status.value = 'idle';
    }
  }

  return {
    status,
    errorMessage,
    fullInfo,
    viewers,
    remoteVideoStream,
    remoteAudioStream,
    stats,
    startPlaying,
    handleAnswer,
    handleError,
    retryNow,
    handleStreamFull,
    handleViewersUpdate,
    stopPlaying,
  };
}
